import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState, useRef } from "react";
import { Match } from "../../models/Match";
import { getMatchesForPlayerBySeason } from "../../services/MatchService";
import MatchTable from "../../components/MatchTabel";
import { fetchSeasonsPlayed } from "../../services/PlayerStatService";
import SeasonScroll from "../../components/SeasonScroll/SeasonScroll";
import Loading from "../../components/Loading";
import useLocalStorage from 'use-local-storage';

const PlayerMatch = () => {
  const { steamId } = useParams<{ steamId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [seasons, setSeasons] = useState<number[]>([]);
  const [isDark] = useLocalStorage<boolean>('isDark', false);
  
  // Use a ref to track the current active request
  const activeRequestRef = useRef<number | null>(null);
  
  // Debug counter to track re-renders
  const renderCount = useRef(0);
  
  // Debug log
  console.log(`Render #${++renderCount.current} - Season: ${location.search}, Loading: ${matchesLoading}, Matches: ${matches.length}`);
  
  // Get the season from URL params or sessionStorage
  const getInitialSeason = () => {
    const params = new URLSearchParams(location.search);
    const seasonParam = params.get('season');
    if (seasonParam) return Number(seasonParam);
    
    const storedSeason = sessionStorage.getItem(`player_${steamId}_season`);
    return storedSeason ? Number(storedSeason) : 0;
  };
  
  const [season, setSeason] = useState<number>(getInitialSeason());

  // Update season handler with URL and storage update
  const handleSeasonChange = (newSeason: number) => {
    console.log(`handleSeasonChange called: ${season} -> ${newSeason}, loading: ${matchesLoading}`);
    
    // Skip if we're already on this season and it's loading
    if (newSeason === season && matchesLoading) {
      console.log("Season change skipped - already loading");
      return;
    }
    
    // Skip if season hasn't changed
    if (newSeason === season) {
      console.log("Season change skipped - same season");
      return;
    }
    
    console.log("Changing season and updating URL");
    
    // IMPORTANT: Clear matches immediately when changing seasons
    // This is the only way to avoid duplicate keys
    setMatches([]);
    
    setSeason(newSeason);
    setMatchesLoading(true);
    
    // Update URL with the new season
    const params = new URLSearchParams(location.search);
    params.set('season', newSeason.toString());
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    
    // Store in sessionStorage
    sessionStorage.setItem(`player_${steamId}_season`, newSeason.toString());
  };

  // Fetch which seasons the player has played - memoized to prevent excessive calls
  const fetchPlayedSeasons = useCallback(async () => {
    if (!steamId) return;
    
    console.log("fetchPlayedSeasons called");
    try {
      const fetchedSeasons = await fetchSeasonsPlayed(steamId);
      // Sort descending (highest season first)
      const sortedSeasons = fetchedSeasons.sort((a, b) => b - a);
      setSeasons(sortedSeasons);
      console.log(`Fetched ${sortedSeasons.length} seasons: ${sortedSeasons.join(', ')}`);

      // If no season is selected yet, default to the highest season
      if (sortedSeasons.length > 0 && season === 0) {
        console.log(`Setting initial season to ${sortedSeasons[0]}`);
        handleSeasonChange(sortedSeasons[0]);
      }
    } catch (error) {
      console.error("Error fetching seasons:", error);
    }
  }, [steamId]); // Remove 'season' from dependency array to prevent re-fetch loop

  // On component mount, fetch all played seasons
  useEffect(() => {
    console.log("Initial seasons fetch effect triggered");
    fetchPlayedSeasons();
  }, [fetchPlayedSeasons]);

  // Whenever the selected season changes, fetch that season's matches
  useEffect(() => {
    if (!steamId || !season) {
      console.log("Skipping matches fetch - no steamId or season");
      return;
    }
    
    console.log(`Matches fetch effect triggered for season ${season}`);
    setMatchesLoading(true);
    
    // Generate a unique ID for this request
    const requestId = Date.now();
    activeRequestRef.current = requestId;
    console.log(`Starting request ${requestId} for season ${season}`);
    
    const fetchMatches = async () => {
      try {
        console.log(`Fetching matches for season ${season}...`);
        const fetchedMatches = await getMatchesForPlayerBySeason(steamId, season);
        
        // Only update state if this is still the active request
        if (activeRequestRef.current === requestId) {
          console.log(`Request ${requestId} completed: ${fetchedMatches.length} matches found`);
          
          // Clear and replace in a single render to avoid flashes
          setMatches(fetchedMatches); 
          setMatchesLoading(false);
          setLoading(false);
        } else {
          console.log(`Request ${requestId} ignored - no longer active`);
        }
      } catch (error) {
        console.error(`Error fetching matches for season ${season}:`, error);
        
        // Only update loading state if this is still the active request
        if (activeRequestRef.current === requestId) {
          console.log(`Request ${requestId} error handling`);
          setMatches([]); // Clear on error
          setMatchesLoading(false);
          setLoading(false);
        }
      }
    };
    
    fetchMatches();
    
    // Cleanup function to handle component unmount or dependency changes
    return () => {
      // Mark any in-progress request as stale
      console.log(`Cleaning up request ${activeRequestRef.current} for season ${season}`);
      if (activeRequestRef.current === requestId) {
        activeRequestRef.current = null;
      }
    };
  }, [season, steamId]);

  return (
    <div>
      {/* Only render the SeasonScroll if we have seasons */}
      {seasons.length > 0 && (
        <SeasonScroll
          seasons={seasons}
          selectedSeason={season}
          onSeasonChange={handleSeasonChange}
          isLoading={matchesLoading}
        />
      )}

      {/* Show a loading indicator when matches are loading but matches array is empty */}
      {matchesLoading && matches.length === 0 ? (
        <div className="position-relative" style={{ minHeight: "200px" }}>
          <div 
            className="d-flex align-items-center justify-content-center"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10
            }}
          >
            <div 
              className="d-flex align-items-center justify-content-center"
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '10px',
                backgroundColor: isDark ? 'rgba(33, 37, 41, 0.7)' : 'rgba(255, 255, 255, 0.7)',
                boxShadow: isDark ? '0 0 15px rgba(0, 0, 0, 0.5)' : '0 0 15px rgba(0, 0, 0, 0.2)'
              }}
            >
              <div className={`spinner-border ${isDark ? 'text-light' : 'text-primary'}`} role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      ) : loading && matches.length === 0 ? (
        <Loading />
      ) : (
        // IMPORTANT: Use the key prop to force complete re-rendering of the table when season changes
        // This prevents React from reusing DOM elements with duplicate keys
        <div key={`season-${season}`}>
          <MatchTable 
            matches={matches} 
            compact={false} 
            season={season} 
          />
        </div>
      )}
    </div>
  );
};

export default PlayerMatch;